import axios from 'axios';
import { AuthSignOut } from 'domain/usecases';
import Cookies from 'js-cookie';
import { WithChildren } from 'presentation/config/helpers/react18MigrationHelpers';
import {
  FC,
  Dispatch,
  useState,
  useContext,
  createContext,
  SetStateAction,
  useCallback,
} from 'react';

import { useFilters } from '../modules/Dashboard/hooks/useFilters';
import { useService } from './useService';

// This file provides some helper functions to get authentication and logged in user.
export interface ILegends {
  ESTUDANTE: { SINGULAR: string; PLURAL: string };
  REITOR: { SINGULAR: string; PLURAL: string };
  CURSO: { SINGULAR: string; PLURAL: string };
  GRUPO: { SINGULAR: string; PLURAL: string };
  LOCAL_ESTAGIO: { SINGULAR: string; PLURAL: string };
  COORDENADOR: { SINGULAR: string; PLURAL: string };
  PROFESSOR: { SINGULAR: string; PLURAL: string };
  PRECEPTOR: { SINGULAR: string; PLURAL: string };
  SEMESTRE: { SINGULAR: string; PLURAL: string };
  UNIDADE_CURRICULAR: { SINGULAR: string; PLURAL: string };
}

export interface IUserModel {
  id: string;
  filterByCurricularUnit: boolean;
  filterBySemester: boolean;
  firstName: string;
  name: string;
  photo: string | null;
  roleName?: string | null;
  activeProfile: {
    id: number;
    legends: ILegends;
    profileId: number | string;
    profileName: string;
    curso?: string | null;
    entity: {
      nome: string;
      cnpj: string;
      email: string;
      id: string;
      logo?: string | null;
    };
  };
}

type AuthContextProps = {
  auth: IUserModel | undefined;
  saveAuth: (auth: IUserModel | undefined) => void;
  currentUser: IUserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<IUserModel | undefined>>;
  logout: () => void;
  getLoggedUser: () => IUserModel | undefined;
};

const initAuthContextPropsState = {
  auth: undefined,
  logout: () => {},
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  getLoggedUser: () => undefined,
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const { clearFilters } = useFilters();
  const getLoggedUser = () => {
    const user = Cookies.get('@rade-gestor/authUser');
    if (!user) {
      return undefined;
    } else {
      return JSON.parse(user);
    }
  };

  /*
    1. Quando o usuário fizer login, criar um identificar de sessão para ele.
    2. Armazenar identificador em um cookie.
    3. Sempre que o usuário fizer uma requisição, usar o id da sessão (extraído do cookie) para recuperar os detalhes da conta
  */

  const [auth, setAuth] = useState<IUserModel | undefined>(getLoggedUser());
  const [currentUser, setCurrentUser] = useState<IUserModel | undefined>(
    getLoggedUser(),
  );

  const { authenticationService } = useService();

  const saveAuth = (auth: IUserModel | undefined) => {
    setAuth(auth);
  };

  const requestAuthSignOut = useCallback(async (params: AuthSignOut.Params) => {
    await authenticationService
      .authSignOut(params)
      .then((res) => {})
      .catch((err) => {});
  }, []);

  const logout = () => {
    saveAuth(undefined);
    setCurrentUser(undefined);
    Cookies.remove('@rade-gestor/authUser');
    clearFilters();
    window.location.href = '/login';
    requestAuthSignOut();
  };

  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (error.response) {
        if (error.response.status === 403) {
          logout();
        }
      }
      return Promise.reject(error);
    },
  );

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        getLoggedUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
